import React from "react";
import { Chip, IconButton, TableCell, TableRow } from "@mui/material";
import { DeleteOutlineOutlined, DragIndicator, KeyboardArrowDown, KeyboardArrowUp, SettingsBackupRestore } from "@mui/icons-material";
import { Draggable } from "@hello-pangea/dnd";
import { ExecutionStep } from "./ExecutionStepFormShared";
import ExecutionStepEditorPanel from "./ExecutionStepEditorPanel";
import { formatDateTime } from "./caseExecutionJobDetailsUtils";
import { translate } from "../../../services/Translations/translatorService";
import caseExecutionJobDetailsPageStyle from "./caseExecutionJobDetailsPageStyle";

type ExecutionJobStepRowProps = {
  index: number;
  isJobPendingApproval: boolean;
  step: ExecutionStep;
  isOpen: boolean;
  isDragDisabled: boolean;
  isStatusTogglePending: boolean;
  onToggleStepOpen: (stepId: number) => void;
  onToggleStepSkipped: (step: ExecutionStep) => Promise<void>;
};

const ExecutionJobStepRow = ({
  index,
  isJobPendingApproval,
  step,
  isOpen,
  isDragDisabled,
  isStatusTogglePending,
  onToggleStepOpen,
  onToggleStepSkipped,
}: ExecutionJobStepRowProps) => {
  const isSkipped = step.status === "skipped";

  return (
    <Draggable draggableId={`execution-step-${step.id}`} index={index} isDragDisabled={isDragDisabled}>
      {(draggableProvided, draggableSnapshot) => (
        <React.Fragment>
          <TableRow
            ref={draggableProvided.innerRef}
            {...draggableProvided.draggableProps}
            style={draggableProvided.draggableProps.style}
            sx={{
              opacity: isSkipped ? 0.65 : 1,
              backgroundColor: draggableSnapshot.isDragging ? "rgba(25, 118, 210, 0.04)" : "transparent",
            }}
          >
            <TableCell padding={"checkbox"}>
              {isJobPendingApproval && (
                <IconButton
                  size={"small"}
                  disabled={isDragDisabled}
                  {...draggableProvided.dragHandleProps}
                  sx={{
                    cursor: isDragDisabled ? "default" : "grab",
                    color: isDragDisabled ? "text.disabled" : "text.secondary",
                  }}
                >
                  <DragIndicator />
                </IconButton>
              )}
            </TableCell>
            <TableCell padding={"checkbox"}>
              <IconButton size={"small"} onClick={() => onToggleStepOpen(step.id)}>
                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </TableCell>
            <TableCell>{step.position}</TableCell>
            <TableCell>
              <Chip
                size={"small"}
                label={translate(`executionJob.step.actionType.values.${step.actionType}`, step.actionType)}
                sx={caseExecutionJobDetailsPageStyle.actionTypeChipSxByActionType[step.actionType]}
              />
            </TableCell>
            <TableCell>
              <Chip
                size={"small"}
                label={translate(`executionJob.step.status.values.${step.status}`, step.status)}
                sx={caseExecutionJobDetailsPageStyle.stepStatusChipSxByStatus[step.status]}
              />
            </TableCell>
            <TableCell>
              {formatDateTime(["completed", "failed", "skipped"].includes(step.status) ? step.executedAt : null)}
            </TableCell>
            <TableCell align={"right"}>
              {isJobPendingApproval && (step.status === "pending" || isSkipped) && (
                <IconButton size={"small"} onClick={() => onToggleStepSkipped(step)} disabled={isStatusTogglePending}>
                  {isSkipped ? <SettingsBackupRestore /> : <DeleteOutlineOutlined />}
                </IconButton>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={7} sx={{ py: 0 }}>
              <ExecutionStepEditorPanel
                step={step}
                isOpen={isOpen}
                isJobPendingApproval={isJobPendingApproval}
              />
            </TableCell>
          </TableRow>
        </React.Fragment>
      )}
    </Draggable>
  );
};

export default ExecutionJobStepRow;
